import { ChevronRight, Plus, Search, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { CreateUserModal } from "../../components/CreateUserModal";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type OrgMembership = {
  role: string;
  orgName: string;
  orgSlug: string;
  orgId: string;
};

type UserRow = {
  id: string;
  name: string;
  email: string | null;
  phoneNumber: string | null;
  phoneNumberVerified: boolean;
  emailVerified: boolean;
  banned: boolean | null;
  isSuperAdmin: boolean | null;
  superAdminRole: string | null;
  createdAt: string;
  organizations: OrgMembership[];
};

function deriveStatus(u: UserRow): "active" | "pending" | "suspended" {
  if (u.banned) return "suspended";
  if (!u.emailVerified && !u.phoneNumberVerified) return "pending";
  return "active";
}

export function BrukereAdminPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchUsers = () => {
    setLoading(true);
    fetch(`${API}/api/admin/users`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setUsers(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      u.name.toLowerCase().includes(q) ||
      (u.email ?? "").toLowerCase().includes(q) ||
      (u.phoneNumber ?? "").includes(q) ||
      u.organizations.some((o) => o.orgName.toLowerCase().includes(q))
    );
  });

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-screen-xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-foreground" style={{ fontFamily: "var(--font-display)" }}>
            Brukere
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {loading ? "Laster..." : `${filtered.length} brukere`}
          </p>
        </div>
        <Button onClick={() => setIsModalOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Legg til bruker
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Søk etter navn, e-post, telefon eller organisasjon..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <CreateUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchUsers}
      />

      {/* Table — desktop */}
      <Card className="hidden sm:block overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bruker</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Telefon</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Organisasjon</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Registrert</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">Laster brukere...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">Ingen brukere funnet</td></tr>
            ) : (
              filtered.map((u) => (
                <tr key={u.id} className="hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate(`/brukere/${u.id}`)}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/8 flex-shrink-0">
                        <span className="text-xs font-bold text-primary">{u.name[0]?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground flex items-center gap-1.5">
                          {u.name}
                          {u.isSuperAdmin && (
                            <Badge variant="accent" className="text-[10px] py-0">
                              {u.superAdminRole === "developer" ? "Developer" : "Admin"}
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{u.email ?? "—"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">{u.phoneNumber ?? "—"}</td>
                  <td className="px-6 py-4">
                    <OrgList orgs={u.organizations} />
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={deriveStatus(u)} />
                  </td>
                  <td className="px-6 py-4 text-muted-foreground">{formatDate(u.createdAt)}</td>
                  <td className="px-6 py-4">
                    <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-3">
        {loading ? (
          <p className="text-center text-sm text-muted-foreground py-8">Laster brukere...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">Ingen brukere funnet</p>
        ) : (
          filtered.map((u) => (
            <Card key={u.id} className="p-4 cursor-pointer" onClick={() => navigate(`/brukere/${u.id}`)}>
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/8 flex-shrink-0">
                  <span className="text-sm font-bold text-primary">{u.name[0]?.toUpperCase()}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email ?? u.phoneNumber ?? "—"}</p>
                    </div>
                    <StatusBadge status={deriveStatus(u)} />
                  </div>
                  <div className="mt-1.5">
                    <OrgList orgs={u.organizations} />
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}

function OrgList({ orgs }: { orgs: OrgMembership[] }) {
  if (orgs.length === 0) return <span className="text-muted-foreground">—</span>;
  if (orgs.length === 1) {
    return (
      <span className="text-foreground">
        {orgs[0].orgName}
        <span className="text-muted-foreground text-xs ml-1.5">({formatRole(orgs[0].role)})</span>
      </span>
    );
  }
  return (
    <span className="text-foreground">
      {orgs[0].orgName}
      <span className="text-muted-foreground text-xs ml-1.5">+{orgs.length - 1} til</span>
    </span>
  );
}

function formatRole(role: string) {
  const map: Record<string, string> = { owner: "Eier", admin: "Admin", member: "Bruker" };
  return map[role] ?? role;
}

function StatusBadge({ status }: { status: "active" | "pending" | "suspended" }) {
  const map = {
    active: { label: "Aktiv", variant: "success" as const },
    pending: { label: "Venter", variant: "warning" as const },
    suspended: { label: "Suspendert", variant: "destructive" as const },
  };
  const { label, variant } = map[status];
  return <Badge variant={variant}>{label}</Badge>;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" });
}
